/**
 * Seeded project JWKS — the structural half of the auth-gateway fix.
 *
 * `supabase.auth.getClaims()` verifies the access-token signature against the
 * project's PUBLIC JWKS. By default it fetches that keyset from the client's
 * configured URL, which for the session/proxy client is the custom-domain
 * gateway `auth.theduchy.com` — the very endpoint that intermittently hangs
 * (see the auth-gateway RCA). That per-cold-isolate JWKS fetch is the DOMINANT,
 * REMOVABLE hot-path dependency: the JWKS is public and non-host-bound, so it
 * does not need the gateway at all.
 *
 * Passing this seed as `getClaims(jwt, { jwks })` makes signature verification
 * use a keyset we sourced OFF the gateway, so a gateway stall can no longer wedge
 * the JWKS half of session verification. (The ~hourly token-refresh POST still
 * goes to the gateway by necessity — that stays bounded by the proxy client's
 * per-attempt retry.) `getClaims` self-heals on key rotation: if the token's
 * `kid` is not in the seed, auth-js falls back to its own network fetch.
 *
 * Source priority:
 *   1. `SUPABASE_AUTH_JWKS` — a committed/env keyset (JSON `{keys:[…]}` or a bare
 *      `[…]` array). Zero network, fully gateway-independent. Preferred in prod.
 *   2. a SINGLE bounded fetch from the DIRECT project endpoint
 *      (`SUPABASE_SERVER_URL`, the reliable path per lib/supabase/url.ts).
 *
 * Returns `undefined` on any miss/failure, so the caller simply omits the option
 * and `getClaims` falls back to its default behaviour — i.e. a strict no-op /
 * no regression when the seed is unavailable.
 *
 * Caching: this is awaited on the auth hot path, so a HIT is cached for an hour
 * (so a signing-key rotation self-heals on a warm isolate) and a MISS is
 * negative-cached briefly (so a slow/unreachable direct endpoint is NOT re-hit on
 * every request — the single-shot fetch below also keeps the worst case to one
 * short timeout, never a multi-retry amplification on the hot path).
 */
type Jwks = { keys: JsonWebKey[] }

const JWKS_PATH = '/auth/v1/.well-known/jwks.json'
// Single-shot bound. Short, because this blocks getClaims on the middleware hot
// path — if the seed isn't quickly available we fall back to the gateway rather
// than delay the request.
const SEED_FETCH_TIMEOUT_MS = 2500
const HIT_TTL_MS = 60 * 60 * 1000 // 1h — re-seed so key rotation heals
const MISS_TTL_MS = 60 * 1000 // 60s — don't re-pay a stalling endpoint per request

let cache: { seed: Jwks | undefined; at: number } | undefined
let inflight: Promise<Jwks | undefined> | undefined

export function getSeededJwks(now: number = Date.now()): Promise<Jwks | undefined> {
  if (cache) {
    const ttl = cache.seed ? HIT_TTL_MS : MISS_TTL_MS
    if (now - cache.at < ttl) return Promise.resolve(cache.seed)
  }
  if (inflight) return inflight
  inflight = loadSeed()
    .then((seed) => {
      cache = { seed, at: Date.now() }
      return seed
    })
    .catch(() => {
      cache = { seed: undefined, at: Date.now() }
      return undefined
    })
    .finally(() => {
      inflight = undefined
    })
  return inflight
}

async function loadSeed(): Promise<Jwks | undefined> {
  // 1. Committed/env keyset — fully offline.
  const raw = process.env.SUPABASE_AUTH_JWKS
  if (raw) {
    const parsed = parseJwks(raw)
    if (parsed) return parsed
    // malformed env value → fall through to the network seed rather than throw
  }

  // 2. A SINGLE bounded fetch from the DIRECT endpoint (never the custom-domain
  // gateway, and NOT via fetchWithRetry — a per-attempt retry here would stack
  // multiple timeouts on the hot path). SUPABASE_SERVER_URL is the direct
  // `<ref>.supabase.co` URL; if unset we return undefined and let getClaims fall
  // back to its own fetch.
  const base = process.env.SUPABASE_SERVER_URL
  if (!base) return undefined

  const url = `${base.replace(/\/$/, '')}${JWKS_PATH}`
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(SEED_FETCH_TIMEOUT_MS) })
    if (!res.ok) return undefined
    return normalizeJwks((await res.json()) as unknown)
  } catch {
    // Swallow transient/timeout faults — the seed is an optimization, not a hard
    // dependency; getClaims falls back to the gateway JWKS fetch.
    return undefined
  }
}

function parseJwks(raw: string): Jwks | undefined {
  try {
    return normalizeJwks(JSON.parse(raw))
  } catch {
    return undefined
  }
}

function normalizeJwks(value: unknown): Jwks | undefined {
  const keys = Array.isArray(value)
    ? value
    : (value as { keys?: unknown } | null)?.keys
  if (Array.isArray(keys) && keys.length > 0) {
    return { keys: keys as JsonWebKey[] }
  }
  return undefined
}

/** Test-only: drop the cache (e.g. to re-seed after key rotation). */
export function _resetSeededJwks(): void {
  cache = undefined
  inflight = undefined
}
